
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Reply, Trash2 } from 'lucide-react';
import { Message } from '@/types/chat';

interface MessageActionMenuProps {
  message: Message;
  isCurrentUser: boolean;
  onReply: (message: Message) => void;
  onDelete: (messageId: string) => void;
  children: React.ReactNode;
}

export const MessageActionMenu: React.FC<MessageActionMenuProps> = ({
  message,
  isCurrentUser,
  onReply,
  onDelete,
  children
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout>();
  const touchStartTimeRef = useRef<number>(0);
  const touchStartPositionRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Check if click is on interactive element that should not trigger menu
  const isInteractiveElement = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof HTMLElement)) return false;

    // List of selectors that should not trigger the menu
    const interactiveSelectors = [
      'button',
      '[role="button"]',
      '.audio-play-button',
      '.audio-progress-bar',
      '.audio-slider',
      'input[type="range"]',
      '.progress-bar',
      '[data-interactive="true"]',
      'a' // Links should not trigger the menu
    ];

    // Check if the element or its parents match interactive selectors
    let element: HTMLElement | null = target;
    while (element) {
      for (const selector of interactiveSelectors) {
        if (element.matches?.(selector)) {
          return true;
        }
      }
      
      // Check for specific classes that indicate interactive elements
      if (element.classList.contains('audio-control') ||
          element.classList.contains('progress-control') ||
          element.classList.contains('play-pause-button')) {
        return true;
      }

      element = element.parentElement;
    }

    return false;
  }, []);

  // Check if click is in the central area of an image (80% center)
  const isImageCenterClick = useCallback((event: MouseEvent | TouchEvent, target: EventTarget | null): boolean => {
    if (!target || !(target instanceof HTMLElement)) return false;

    // Find the image element
    let imgElement: HTMLImageElement | null = null;
    let element: HTMLElement | null = target;
    
    while (element && !imgElement) {
      if (element.tagName === 'IMG') {
        imgElement = element as HTMLImageElement;
      } else {
        // Look for img children
        imgElement = element.querySelector('img');
      }
      if (!imgElement) {
        element = element.parentElement;
      }
    }

    if (!imgElement) return false;

    const rect = imgElement.getBoundingClientRect();
    const clientX = 'clientX' in event ? event.clientX : event.touches[0].clientX;
    const clientY = 'clientY' in event ? event.clientY : event.touches[0].clientY;

    // Calculate click position relative to image
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    // Calculate 80% center area (10% margin on each side)
    const marginX = rect.width * 0.1;
    const marginY = rect.height * 0.1;

    const isInCenterX = relativeX >= marginX && relativeX <= (rect.width - marginX);
    const isInCenterY = relativeY >= marginY && relativeY <= (rect.height - marginY);

    return isInCenterX && isInCenterY;
  }, []);

  const showMenu = useCallback((clientX: number, clientY: number) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = 120;
    const menuHeight = 80;

    let x = clientX;
    let y = clientY;

    // Adjust position to keep menu within viewport
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10;
    }
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 10;
    }

    setMenuPosition({ x, y });
    setIsMenuOpen(true);
  }, []);

  // Desktop handlers
  const handleMouseClick = useCallback((event: React.MouseEvent) => {
    // Check if click is on interactive element
    if (isInteractiveElement(event.target)) {
      return;
    }

    // For images, check if click is in center area
    if (message.content_type === 'image' && isImageCenterClick(event.nativeEvent, event.target)) {
      return;
    }

    // Both left and right click show menu
    event.preventDefault();
    event.stopPropagation();
    showMenu(event.clientX, event.clientY);
  }, [showMenu, isInteractiveElement, isImageCenterClick, message.content_type]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    // Check if click is on interactive element
    if (isInteractiveElement(event.target)) {
      return;
    }

    // For images, check if click is in center area
    if (message.content_type === 'image' && isImageCenterClick(event.nativeEvent, event.target)) {
      return;
    }

    // Prevent browser context menu
    event.preventDefault();
    event.stopPropagation();
    showMenu(event.clientX, event.clientY);
  }, [showMenu, isInteractiveElement, isImageCenterClick, message.content_type]);

  // Mobile handlers
  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    touchStartTimeRef.current = Date.now();
    touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY };
    hasMovedRef.current = false;
    
    // Set timer for long press (600ms)
    longPressTimerRef.current = setTimeout(() => {
      if (!hasMovedRef.current && !isInteractiveElement(event.target)) {
        // For images, check if touch is in center area
        if (message.content_type === 'image' && isImageCenterClick(event.nativeEvent, event.target)) {
          return;
        }
        
        event.preventDefault();
        showMenu(touch.clientX, touch.clientY);
      }
    }, 600);
  }, [showMenu, isInteractiveElement, isImageCenterClick, message.content_type]);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    const startPos = touchStartPositionRef.current;
    const moveDistance = Math.sqrt(
      Math.pow(touch.clientX - startPos.x, 2) + 
      Math.pow(touch.clientY - startPos.y, 2)
    );

    // If user moved more than 10px, cancel long press (they're scrolling)
    if (moveDistance > 10) {
      hasMovedRef.current = true;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    }
  }, []);

  const handleTouchEnd = useCallback((event: React.TouchEvent) => {
    const touchDuration = Date.now() - touchStartTimeRef.current;
    
    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    // Short tap (less than 300ms) and hasn't moved much
    if (touchDuration < 300 && !hasMovedRef.current && !isInteractiveElement(event.target)) {
      // For images, check if touch is in center area
      if (message.content_type === 'image' && isImageCenterClick(event.nativeEvent, event.target)) {
        return;
      }

      const touch = event.changedTouches[0];
      event.preventDefault();
      event.stopPropagation();
      showMenu(touch.clientX, touch.clientY);
    }
  }, [showMenu, isInteractiveElement, isImageCenterClick, message.content_type]);

  const handleReply = useCallback(() => {
    onReply(message);
    setIsMenuOpen(false);
  }, [onReply, message]);

  const handleDelete = useCallback(() => {
    if (window.confirm('Tem certeza que deseja apagar esta mensagem?')) {
      onDelete(message.id);
    }
    setIsMenuOpen(false);
  }, [onDelete, message.id]);

  return (
    <>
      <div
        ref={containerRef}
        onClick={handleMouseClick}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="cursor-pointer select-none"
      >
        {children}
      </div>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className="fixed z-[100] bg-black/90 border border-white/20 backdrop-blur-sm rounded-lg shadow-xl py-1 min-w-[120px]"
          style={{
            left: menuPosition.x,
            top: menuPosition.y,
          }}
        >
          <button
            onClick={handleReply}
            className="w-full px-3 py-2 text-left text-white hover:bg-white/10 flex items-center gap-2 text-sm transition-colors"
          >
            <Reply className="h-4 w-4" />
            Responder
          </button>
          {isCurrentUser && (
            <button
              onClick={handleDelete}
              className="w-full px-3 py-2 text-left text-red-400 hover:bg-red-500/20 flex items-center gap-2 text-sm transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Apagar
            </button>
          )}
        </div>
      )}
    </>
  );
};
